import React from 'react';
import { toast } from 'react-toastify';
import { ToastOptions, ToastContentProps } from '@/types/components';
import { ToastType } from '@/types/common';

const defaultToastOptions: ToastOptions = {
  className: 'w-fit',
  position: 'bottom-center',
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
  theme: 'colored',
};

const ToastContent: React.FC<ToastContentProps> = ({ title, message }) => {
  const safeSentences: string[] =
    message
      ?.split('.')
      ?.map((s: string) => s?.trim())
      ?.filter(Boolean) || [];

  return (
    <div className="flex flex-col max-w-[300px]">
      <strong className="font-sans text-lg font-medium whitespace-nowrap">
        {title || 'Notification'}
      </strong>
      <div className="font-sans text-sm font-regular">
        {safeSentences.length > 0 ? (
          safeSentences.map((sentence: string, idx: number) => (
            <p key={idx}>{sentence}.</p>
          ))
        ) : (
          <p>{message || 'No message provided'}</p>
        )}
      </div>
    </div>
  );
};

let lastCall = 0;

export const showToast = (
  type: ToastType,
  title: string,
  message: string,
  options?: Partial<ToastOptions>
): void => {
  const now = Date.now();

  if (now - lastCall < 100) {
    return;
  }
  lastCall = now;

  if (!type || typeof toast[type] !== 'function') {
    return;
  }

  const safeTitle = title || 'Notification';
  const safeMessage = message || 'No message provided';

  try {
    const toastId = `toast-${now}`;

    const toastOptions: ToastOptions = {
      ...defaultToastOptions,
      ...options,
      toastId: toastId,
    };

    toast[type](
      <ToastContent title={safeTitle} message={safeMessage} />,
      toastOptions
    );
  } catch (error) {
    try {
      toast[type](`${safeTitle}: ${safeMessage}`, {
        position: 'bottom-right',
        autoClose: 5000,
        toastId: `fallback-${now}`,
      });
    } catch (fallbackError) {}
  }
};

export const showSuccessToast = (
  title: string,
  message: string,
  options?: Partial<ToastOptions>
): void => {
  showToast('success', title, message, options);
};

export const showErrorToast = (
  title: string,
  message: string,
  options?: Partial<ToastOptions>
): void => {
  showToast('error', title, message, options);
};

export const showWarningToast = (
  title: string,
  message: string,
  options?: Partial<ToastOptions>
): void => {
  showToast('warning', title, message, options);
};

export const showInfoToast = (
  title: string,
  message: string,
  options?: Partial<ToastOptions>
): void => {
  showToast('info', title, message, options);
};

/**
 * The API layer always rethrows a plain `Error` carrying the backend message
 * (see utils/errorHandler), so a catch block only needs the fallback copy.
 */
export const showCaughtErrorToast = (
  error: unknown,
  fallback: string
): void => {
  showToast(
    'error',
    'Gagal',
    error instanceof Error ? error.message : fallback
  );
};

export const cleanupToasts = (): void => {
  try {
    toast.dismiss();
  } catch (error) {}
};

const GlobalToast = {
  showToast,
  showSuccessToast,
  showErrorToast,
  showWarningToast,
  showInfoToast,
  cleanupToasts,
};

export default GlobalToast;
